import React, { useCallback, useContext, useEffect, useState } from 'react';
import GenericTable from '../components/GenericTable';
import ModalForm from '../components/ModalForm';
import SupplierDetails from '../components/modals/SupplierDetails';
import GenericFilters from '../components/GenericFilters';
import { supplierService } from '../services/supplierService';
import { notifyError, notifySuccess, notifyWarning } from '../utils/notifications';
import { UserContext } from '../utils/UserContext';
import { MdAddBusiness } from "react-icons/md";


const Customer = () => {
    const { selectedWorkshop } = useContext(UserContext);
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [workshopID, setWorkshopID] = useState(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [supplierIdToDelete, setSupplierIdToDelete] = useState(null);

    useEffect(() => {
        if (selectedWorkshop) {
            setWorkshopID(selectedWorkshop._id);
        } else {
            notifyWarning("No se ha seleccionado un taller.");
        }
    }, [selectedWorkshop]);

    const fetchSuppliers = useCallback(async () => {
        if (!workshopID) return;
        setIsLoading(true);

        try {
            const suppliersData = await supplierService.getSuppliers(workshopID);
            const suppliers = suppliersData.map((supplier) => ({
                supplier_id: supplier.id,
                nombreProveedor: supplier.name,
                nombreEncargado: supplier.manager_name,
                direccion: supplier.address,
                telefono: supplier.phone,
                rfc: supplier.RFC || '',
                telefonoSecundario: supplier.secondary_phone || '',
                email: supplier.email || '',
            }));
            setData(suppliers);
            setFilteredData(suppliers);
        } catch (error) {
            console.error('Error al obtener proveedores:', error);
            notifyError('Error al cargar proveedores');
        } finally {
            setIsLoading(false);
        }
    }, [workshopID]);

    useEffect(() => {
        fetchSuppliers();
    }, [fetchSuppliers]);

    useEffect(() => {
        setFilteredData(data);
    }, [data]);

    const handleAddSupplier = async (formData) => {
        if (isSubmitting) return;
        
        const { nombreProveedor, nombreEncargado, direccion, telefono, rfc, telefonoSecundario, email } = formData;

        if (!nombreProveedor || !nombreEncargado || !direccion || !telefono || !rfc || !email) {
            notifyWarning('Todos los campos son obligatorios.');
            return;
        }

        setIsSubmitting(true);

        const payload = {
            name: nombreProveedor,
            manager_name: nombreEncargado,
            address: direccion,
            phone: telefono,
            RFC: rfc,
            secondary_phone: telefonoSecundario || null,
            email: email,
        };

        try {
            await supplierService.createSupplier(payload, workshopID);
            setIsModalOpen(false);
            fetchSuppliers();
            notifySuccess('Proveedor agregado correctamente.');
        } catch (error) {
            console.error('Error al agregar proveedor:', error);
            notifyError('No se pudo agregar el proveedor.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleViewDetails = (rowData) => {
        setSelectedSupplier(rowData);
        setIsDetailModalOpen(true);
    }

    const actionHandlers = {
        edit: handleViewDetails,
        delete: (supplier) => setSupplierIdToDelete(supplier.supplier_id),
    };

    const handleDeleteSupplier = async () => {
        if (!supplierIdToDelete) return;

        try {
            await supplierService.deleteSupplier(supplierIdToDelete, workshopID);
            setData(prevData => prevData.filter(supplier => supplier.supplier_id !== supplierIdToDelete));
            setFilteredData(prevData => prevData.filter(supplier => supplier.supplier_id !== supplierIdToDelete));
            notifySuccess('Proveedor eliminado correctamente.');
            setSupplierIdToDelete(null);
        } catch (error) {
            console.error('Error al eliminar proveedor:', error);
            notifyError('No se pudo eliminar el proveedor.');
        }
    };

    const handleCancelDelete = () => {
        setSupplierIdToDelete(null);
    };

    const handleFilterChange = (filters) => {
        const search = filters.search?.trim().toLowerCase();

        if (!search) {
            setFilteredData(data);
            return;
        }

        const filtered = data.filter(supplier => {
            const searchIn = [
                supplier.nombreProveedor || '',
                supplier.nombreEncargado || '',
                supplier.email || '',
                supplier.telefono || '',
                supplier.telefonoSecundario || '',
                supplier.direccion || '',
                supplier.rfc || '',
            ].join(' ').toLowerCase();

            return searchIn.includes(search);
        });

        setFilteredData(filtered);
    };

    const handleClearFilters = () => {
        setFilteredData(data);
    };

    const columns = React.useMemo(() => [
        { Header: 'Nombre del Proveedor', accessor: 'nombreProveedor' },
        { Header: 'Nombre del Encargado', accessor: 'nombreEncargado' },
        { Header: 'Dirección', accessor: 'direccion' },
        { Header: 'Teléfono', accessor: 'telefono' },
        { Header: 'RFC', accessor: 'rfc' },
        { Header: 'Correo Electrónico', accessor: 'email' },
    ], []);

    return (
        <div className='category-container'>
            <div className="category-header">
                <h1 className="category-title">Proveedores</h1>
                <button
                    className="add-btn add-btn-icon"
                    onClick={() => setIsModalOpen(true)}
                    title="Agregar proveedor"
                >
                    <MdAddBusiness aria-hidden="true" />
                    <span>Nuevo proveedor</span>
                </button>
            </div>
            <GenericFilters
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                searchPlaceholder="Proveedor, encargado, email, teléfono, dirección..."
                filterLabel="Buscar Proveedores"
            />
            <GenericTable
                columns={columns}
                data={filteredData}
                isLoading={isLoading}
                actionList={['edit', 'delete']}
                actionHandlers={actionHandlers}
            />
            <ModalForm
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleAddSupplier}
                title="Añadir Proveedor"
                fields={[
                    { name: 'nombreProveedor', label: 'Nombre del Proveedor', placeholder: 'Ej. AutosAccesorios', required: true },
                    { name: 'nombreEncargado', label: 'Nombre del Encargado', placeholder: 'Ej. Emiliano Zapata', required: true },
                    { name: 'direccion', label: 'Dirección', placeholder: 'Ej. Calle 24, Zona Real', required: true },
                    { name: 'telefono', label: 'Teléfono', placeholder: 'Ej. 1122334455', required: true, type: 'tel' },
                    { name: 'telefonoSecundario', label: 'Teléfono Secundario', placeholder: 'Ej. 1122334455', type: 'tel' },
                    { name: 'rfc', label: 'RFC', placeholder: 'Ej. ABC123456789', required: true },
                    { name: 'email', label: 'Correo Electrónico', placeholder: 'Ej. tucorreo@dominio.com', required: true, type: 'email' },
                ]}
            />
            {isDetailModalOpen && selectedSupplier && (
                <div className="modal-overlay">
                    <div className="modal-container modal-large">
                        <button className="modal-close-btn" onClick={() => setIsDetailModalOpen(false)}>
                            ×
                        </button>
                        <div className="modal-header">
                            <h2>Detalles del Proveedor</h2>
                        </div>
                        <div className="modal-body">
                            <SupplierDetails 
                                supplierData={selectedSupplier}
                                workshopID={workshopID}
                                onClose={() => setIsDetailModalOpen(false)}
                            />
                        </div>
                    </div>
                </div>
            )}
            {supplierIdToDelete && (
                <ModalForm
                    isOpen={true}
                    onClose={handleCancelDelete}
                    title="Confirmar Eliminación"
                    size="small"
                    footer={
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button className="cancel-btn" onClick={handleCancelDelete}>
                                Cancelar
                            </button>
                            <button
                                className="delete-btn"
                                style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
                                onClick={handleDeleteSupplier}
                            >
                                Eliminar
                            </button>
                        </div>
                    }
                >
                    <p>¿Estás realmente seguro de que deseas eliminar este proveedor? Esta acción no se puede deshacer.</p>
                </ModalForm>
            )}
        </div>
    );
}

export default Customer;
